import { compact } from 'lodash-es';
import { formatPaymentDetailsList, getStatusLabel } from '~/business-logic/invoiceLogic';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import { DateFilterPickerPresets, getFormattedTimeRange } from '~/components/InvoicesTable/DateFilterPicker';
import { invoiceSummaryModel } from '~/models';
export var ClientInvoicesHeaderId = /*#__PURE__*/ function(ClientInvoicesHeaderId) {
    ClientInvoicesHeaderId["InvoiceId"] = "invoiceId";
    ClientInvoicesHeaderId["AgreementName"] = "agreementName";
    ClientInvoicesHeaderId["Status"] = "status";
    ClientInvoicesHeaderId["AdditionalInfo"] = "additionalInfo";
    ClientInvoicesHeaderId["IssueDate"] = "issueDate";
    ClientInvoicesHeaderId["DueDate"] = "dueDate";
    ClientInvoicesHeaderId["Total"] = "total";
    return ClientInvoicesHeaderId;
}({});
export function getClientInvoicesTableDefs(param) {
    let { texts, showAgreementName } = param;
    const entries = compact([
        {
            headerItem: {
                id: "invoiceId",
                value: texts.invoiceId
            },
            columnDef: {
                width: 140,
                sortable: true,
                mobile: {
                    value: {
                        weight: 'bold',
                        skin: 'standard'
                    }
                },
                bi: {
                    name: 'sort_invoice_id'
                }
            }
        },
        showAgreementName && {
            headerItem: {
                id: "agreementName",
                value: texts.agreementName
            },
            columnDef: {
                width: [
                    150,
                    200
                ],
                sortable: true,
                mobile: {},
                bi: {
                    name: 'sort_agreement_name'
                }
            }
        },
        {
            headerItem: {
                id: "status",
                value: texts.invoiceStatus
            },
            columnDef: {
                width: [
                    96,
                    140
                ],
                sortable: true,
                mobile: {
                    hide: true
                },
                bi: {
                    name: 'sort_invoice_status'
                }
            }
        },
        {
            headerItem: {
                id: "additionalInfo",
                value: texts.additionalInfo
            },
            columnDef: {
                width: [
                    160,
                    220
                ],
                sortable: false,
                mobile: {}
            }
        },
        {
            headerItem: {
                id: "issueDate",
                value: texts.issueDate
            },
            columnDef: {
                width: [
                    80,
                    100
                ],
                sortable: true,
                mobile: {
                    label: {
                        text: texts.issueDate
                    }
                },
                bi: {
                    name: 'sort_issue_date'
                }
            }
        },
        {
            headerItem: {
                id: "dueDate",
                value: texts.dueDate
            },
            columnDef: {
                width: [
                    80,
                    100
                ],
                sortable: true,
                mobile: {
                    hide: true
                },
                bi: {
                    name: 'sort_due_date'
                }
            }
        },
        {
            headerItem: {
                id: "total",
                value: texts.total
            },
            columnDef: {
                width: 84,
                sortable: true,
                hAlign: 'end',
                mobile: {
                    divider: true,
                    spaceBetween: true,
                    label: {
                        text: texts.total,
                        hideColon: true,
                        skin: 'standard',
                        weight: 'normal'
                    },
                    value: {
                        weight: 'normal',
                        skin: 'standard'
                    }
                },
                bi: {
                    name: 'sort_total'
                }
            }
        }
    ]);
    return {
        columnDefs: entries.map((e)=>e.columnDef),
        headerItems: entries.map((e)=>e.headerItem)
    };
}
export const EMPTY_FILTERS = {
    search: '',
    statuses: [],
    paymentDetails: [],
    qbo: AccountingStatusFilter.All,
    xero: AccountingStatusFilter.All
};
export function isAdditionalInfoMatchingSearch(invoice, search) {
    const text = invoice.paymentDetails.map((d)=>d.type).join(' ');
    return text.toLowerCase().includes(search);
}
function matchesAccountingStatusFilter(isSynced, filter) {
    return filter === AccountingStatusFilter.All || isSynced === (filter === AccountingStatusFilter.Synced);
}
function matchesPaymentDetailsFilter(invoice, paymentDetails) {
    return paymentDetails.length === 0 || invoice.paymentDetails.some((detail)=>paymentDetails.includes(detail.type));
}
export function applyClientInvoicesFilters(invoices, filters) {
    const search = filters.search.trim().toLowerCase();
    return invoices.filter((inv)=>{
        if (filters.statuses.length > 0 && !filters.statuses.includes(inv.displayStatusV2)) {
            return false;
        }
        if (filters.issuedOnSince && inv.rawIssuedOn < filters.issuedOnSince) {
            return false;
        }
        if (filters.issuedOnUntil && inv.rawIssuedOn > filters.issuedOnUntil) {
            return false;
        }
        if (!matchesPaymentDetailsFilter(inv, filters.paymentDetails)) {
            return false;
        }
        if (!matchesAccountingStatusFilter(invoiceSummaryModel.isSynced(inv), filters.qbo)) {
            return false;
        }
        if (!matchesAccountingStatusFilter(inv.xeroIntegration.isSynced(), filters.xero)) {
            return false;
        }
        if (search.length === 0) {
            return true;
        }
        if (inv.formattedNumber.toLowerCase().includes(search)) return true;
        if (invoiceSummaryModel.relationshipTitle(inv).toLowerCase().includes(search)) return true;
        if (isAdditionalInfoMatchingSearch(inv, search)) return true;
        return false;
    });
}
function countBySyncState(invoices, isSynced) {
    const syncedCount = invoices.filter(isSynced).length;
    return {
        sync: syncedCount,
        unsync: invoices.length - syncedCount
    };
}
export function getClientInvoicesFilterCounters(invoices) {
    const status = {};
    const paymentDetails = {};
    invoices.forEach((inv)=>{
        var _status_inv_displayStatusV2;
        status[inv.displayStatusV2] = ((_status_inv_displayStatusV2 = status[inv.displayStatusV2]) !== null && _status_inv_displayStatusV2 !== void 0 ? _status_inv_displayStatusV2 : 0) + 1;
        inv.paymentDetails.forEach((detail)=>{
            var _paymentDetails_detail_type;
            paymentDetails[detail.type] = ((_paymentDetails_detail_type = paymentDetails[detail.type]) !== null && _paymentDetails_detail_type !== void 0 ? _paymentDetails_detail_type : 0) + 1;
        });
    });
    return {
        status,
        paymentDetails,
        qbo: countBySyncState(invoices, (inv)=>invoiceSummaryModel.isSynced(inv)),
        xero: countBySyncState(invoices, (inv)=>inv.xeroIntegration.isSynced())
    };
}
export function applyClientInvoicesSort(invoices, sort, sortIds) {
    if (sort == null) return invoices;
    const id = sortIds[sort.colIndex];
    if (id == null) return invoices;
    const direction = sort.sort === 'asc' ? 1 : -1;
    const copy = invoices.slice();
    copy.sort((a, b)=>{
        const cmp = compareInvoices(a, b, id);
        return cmp * direction;
    });
    return copy;
}
function compareInvoices(a, b, id) {
    switch(id){
        case "invoiceId":
            return a.formattedNumber.localeCompare(b.formattedNumber);
        case "agreementName":
            return invoiceSummaryModel.relationshipTitle(a).localeCompare(invoiceSummaryModel.relationshipTitle(b));
        case "status":
            return a.displayStatusV2.localeCompare(b.displayStatusV2);
        case "issueDate":
            return a.rawIssuedOn.getTime() - b.rawIssuedOn.getTime();
        case "dueDate":
            return a.rawDueDate.getTime() - b.rawDueDate.getTime();
        case "total":
            return a.rawTotalAmount.comparedTo(b.rawTotalAmount);
        default:
            return 0;
    }
}
export const DEFAULT_SORT = {
    colIndex: 4,
    sort: 'desc'
};
export function getAdditionalInfoText(invoice, texts) {
    return formatPaymentDetailsList(invoice.paymentDetails, texts.paymentDetailLabels, texts.labelOnDate);
}
export function getStatusText(invoice, texts) {
    return getStatusLabel(invoiceSummaryModel.displayStatus(invoice), texts.displayedStatusLabels);
}
export function parseDateRangeFilter(value, now) {
    const presets = Object.values(DateFilterPickerPresets);
    if (typeof value !== 'string' || !presets.includes(value)) return {};
    const { fromDate, toDate } = getFormattedTimeRange(value, now);
    return {
        since: fromDate != null ? new Date(fromDate) : undefined,
        until: toDate != null ? new Date(toDate) : undefined
    };
}
export function distinctAgreementCount(invoices) {
    const set = new Set();
    invoices.forEach((inv)=>set.add(invoiceSummaryModel.relationshipId(inv)));
    return set.size;
}
